<template>
  <o-card>
    <template slot="header">
      <h5 class="mb-0">{{ $t('sandoz.kuko_conditions') }}</h5>
    </template>

    <div v-if="loading" class="text-center py-4">
      <b-spinner small/>
    </div>

    <b-alert v-else-if="!kuko" show variant="info">
      {{ $t('sandoz.kuko_not_yet_created') }}
    </b-alert>

    <div v-else>
      <p class="text-muted small">
        {{ $t('sandoz.kuko_channel') }}: <strong>{{ kuko.channel || '-' }}</strong>
        &middot;
        {{ $t('sandoz.kuko_payment_terms') }}: <strong>{{ kuko.payment_terms || '-' }}</strong>
      </p>

      <b-tabs>
        <b-tab :title="$t('sandoz.kuko_products')" active>
          <condition-table :rows="conditionProducts"
                           :readonly="readonly"
                           :columns="productColumns"
                           rebate-key="condition_product_rebates"
                           @add="addRow('products', { product_id: null })"
                           @update="(row, patch) => updateRow('products', row, patch)"
                           @remove="row => removeRow('products', row)"
                           @add-rebate="(row, rebate) => addRebate('products', row, rebate)"
                           @remove-rebate="(row, rebate) => removeRebate('products', row, rebate)"/>
        </b-tab>
        <b-tab :title="$t('sandoz.kuko_product_groups')">
          <condition-table :rows="conditionProductGroups"
                           :readonly="readonly"
                           :columns="productGroupColumns"
                           rebate-key="condition_product_group_rebates"
                           @add="addRow('product-groups', { product_group_id: null })"
                           @update="(row, patch) => updateRow('product-groups', row, patch)"
                           @remove="row => removeRow('product-groups', row)"
                           @add-rebate="(row, rebate) => addRebate('product-groups', row, rebate)"
                           @remove-rebate="(row, rebate) => removeRebate('product-groups', row, rebate)"/>
        </b-tab>
        <b-tab :title="$t('sandoz.kuko_special_products')">
          <condition-table :rows="conditionSpecialProducts"
                           :readonly="readonly"
                           :columns="specialProductColumns"
                           rebate-key="condition_special_product_rebates"
                           @add="addRow('special-products', { product_id: null, quantity: 0, efp: 0 })"
                           @update="(row, patch) => updateRow('special-products', row, patch)"
                           @remove="row => removeRow('special-products', row)"
                           @add-rebate="(row, rebate) => addRebate('special-products', row, rebate)"
                           @remove-rebate="(row, rebate) => removeRebate('special-products', row, rebate)"/>
        </b-tab>
      </b-tabs>
    </div>

    <b-alert v-if="error" show variant="danger" class="mt-3">{{ error }}</b-alert>
  </o-card>
</template>

<script>
import axios from 'axios'
import sectionMixin from '../sectionMixin'

const COMMON_COLS = [
  { key: 'existing_condition', label: 'existing_condition', type: 'number' },
  { key: 'new_condition', label: 'new_condition', type: 'number' },
  { key: 'valid_from', label: 'valid_from', type: 'date' },
  { key: 'valid_to', label: 'valid_to', type: 'date' }
]

/**
 * Full inline editor for KUKO conditions (products / product groups / special products),
 * including up to 5 rebate rows per condition.
 *
 * Per the plan, this section deliberately OMITS:
 *  - any submit-for-approval / approver assignment UI
 *  - per-KUKO status display (the contract's status block is the only approval surface)
 *  - link/unlink-KUKO actions
 *
 * Conditions persist via the nested /kuko-conditions endpoints — not the contract save —
 * so an edit is immediately reflected in /api/sandoz/contracts/{id}/kuko-conditions and on
 * the legacy /kukos page (read-only there for contract-owned KUKOs).
 */
export default {
  name: 'SectionKuko',
  mixins: [sectionMixin],
  components: {
    ConditionTable: {
      props: ['rows', 'columns', 'rebateKey', 'readonly'],
      template: `
        <div>
          <table class="table table-sm" v-if="rows.length">
            <thead>
              <tr>
                <th v-for="col in columns" :key="col.key">{{ col.label }}</th>
                <th>Rebates</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in rows" :key="row.id">
                <td v-for="col in columns" :key="col.key">
                  <b-form-input v-if="!readonly"
                                :value="row[col.key]"
                                :type="col.type || 'text'"
                                size="sm"
                                @change="$emit('update', row, { [col.key]: $event })"/>
                  <span v-else>{{ row[col.key] }}</span>
                </td>
                <td>
                  <div v-for="rebate in (row[rebateKey] || [])" :key="rebate.id" class="d-flex align-items-center mb-1">
                    <span class="mr-1">{{ rebate.min_quantity }} → {{ rebate.rebate }}%</span>
                    <b-btn v-if="!readonly" size="sm" variant="link" @click="$emit('remove-rebate', row, rebate)">
                      <fa icon="times"/>
                    </b-btn>
                  </div>
                  <b-btn v-if="!readonly && (row[rebateKey] || []).length < 5" size="sm" variant="outline-primary"
                         @click="addPrompt(row)">
                    <fa icon="plus"/> rebate
                  </b-btn>
                </td>
                <td class="text-right">
                  <b-btn v-if="!readonly" size="sm" variant="link" @click="$emit('remove', row)">
                    <fa icon="times"/>
                  </b-btn>
                </td>
              </tr>
            </tbody>
          </table>
          <p v-else class="text-muted">No conditions yet.</p>
          <b-btn v-if="!readonly" size="sm" variant="primary" @click="$emit('add')">
            <fa icon="plus"/> Add row
          </b-btn>
        </div>
      `,
      methods: {
        addPrompt(row) {
          const min = window.prompt('Minimum quantity?')
          if (min === null) {
            return
          }
          const rebate = window.prompt('Rebate %?')
          if (rebate === null) {
            return
          }
          this.$emit('add-rebate', row, { min_quantity: Number(min), rebate: Number(rebate) })
        }
      }
    }
  },
  data() {
    return {
      loading: true,
      error: null,
      kuko: null,
      conditionProducts: [],
      conditionProductGroups: [],
      conditionSpecialProducts: [],
      productColumns: [{ key: 'product_id', label: 'product_id', type: 'number' }, ...COMMON_COLS],
      productGroupColumns: [{ key: 'product_group_id', label: 'product_group_id', type: 'number' }, ...COMMON_COLS],
      specialProductColumns: [
        { key: 'product_id', label: 'product_id', type: 'number' },
        { key: 'quantity', label: 'quantity', type: 'number' },
        { key: 'efp', label: 'efp', type: 'number' },
        ...COMMON_COLS
      ]
    }
  },
  created() {
    this.load()
  },
  watch: {
    'item.id'() {
      this.load()
    }
  },
  methods: {
    base() {
      return `/api/sandoz/contracts/${this.item.id}/kuko-conditions`
    },
    typePath(type) {
      return {
        products: 'products',
        'product-groups': 'product-groups',
        'special-products': 'special-products'
      }[type]
    },
    async load() {
      if (!this.item.id) {
        this.loading = false
        return
      }
      this.loading = true
      this.error = null
      try {
        const resp = await axios.get(this.base())
        this.kuko = resp.data.kuko
        this.conditionProducts = resp.data.condition_products || []
        this.conditionProductGroups = resp.data.condition_product_groups || []
        this.conditionSpecialProducts = resp.data.condition_special_products || []
      } catch (err) {
        this.error = (err.response && err.response.data && err.response.data.message) || err.message
      } finally {
        this.loading = false
      }
    },
    targetCollection(type) {
      return {
        products: 'conditionProducts',
        'product-groups': 'conditionProductGroups',
        'special-products': 'conditionSpecialProducts'
      }[type]
    },
    async addRow(type, payload) {
      this.error = null
      try {
        const resp = await axios.post(`${this.base()}/${this.typePath(type)}`, payload)
        this[this.targetCollection(type)].push({ ...resp.data, [this.rebateKeyFor(type)]: [] })
      } catch (err) {
        this.error = (err.response && err.response.data && err.response.data.message) || err.message
      }
    },
    async updateRow(type, row, patch) {
      this.error = null
      try {
        const resp = await axios.patch(`${this.base()}/${this.typePath(type)}/${row.id}`, patch)
        Object.assign(row, resp.data)
      } catch (err) {
        this.error = (err.response && err.response.data && err.response.data.message) || err.message
      }
    },
    async removeRow(type, row) {
      this.error = null
      try {
        await axios.delete(`${this.base()}/${this.typePath(type)}/${row.id}`)
        const col = this[this.targetCollection(type)]
        const idx = col.indexOf(row)
        if (idx >= 0) {
          col.splice(idx, 1)
        }
      } catch (err) {
        this.error = (err.response && err.response.data && err.response.data.message) || err.message
      }
    },
    async addRebate(type, row, rebate) {
      this.error = null
      try {
        const resp = await axios.post(`${this.base()}/${this.typePath(type)}/${row.id}/rebates`, rebate)
        const key = this.rebateKeyFor(type)
        if (!Array.isArray(row[key])) {
          this.$set(row, key, [])
        }
        row[key].push(resp.data)
      } catch (err) {
        this.error = (err.response && err.response.data && err.response.data.message) || err.message
      }
    },
    async removeRebate(type, row, rebate) {
      this.error = null
      try {
        await axios.delete(`${this.base()}/${this.typePath(type)}/${row.id}/rebates/${rebate.id}`)
        const key = this.rebateKeyFor(type)
        const arr = row[key] || []
        const idx = arr.indexOf(rebate)
        if (idx >= 0) {
          arr.splice(idx, 1)
        }
      } catch (err) {
        this.error = (err.response && err.response.data && err.response.data.message) || err.message
      }
    },
    rebateKeyFor(type) {
      return {
        products: 'condition_product_rebates',
        'product-groups': 'condition_product_group_rebates',
        'special-products': 'condition_special_product_rebates'
      }[type]
    }
  }
}
</script>
